import React, { useEffect, useState } from 'react';
import { Table, Tag, Select, Input, Button, Space, DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { supabase } from '../../supabaseClient';
import dayjs from 'dayjs';
import './style.css';

const { Option } = Select;

const userTypeMap: Record<number, string> = {
  0: '普通用户',
  1: '大节点POW',
  2: '小节点POS',
};
const userTypeColor: Record<number, string> = {
  0: 'default',
  1: 'geekblue',
  2: 'green',
};

const UserList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userTypeFilter, setUserTypeFilter] = useState<string | undefined>();
  const [inviterFilter, setInviterFilter] = useState<string>('');
  const [groupIdFilter, setGroupIdFilter] = useState<string | undefined>();
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  // 新增筛选按钮逻辑
  const [filtering, setFiltering] = useState(false);
  const [userId, setUserId] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userAddress, setUserAddress] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [deviceCountMap, setDeviceCountMap] = useState<Record<string, { total: number, wait: number, success: number, fail: number }>>({});
  const [depositMap, setDepositMap] = useState<Record<string, number>>({});
  const [withdrawMap, setWithdrawMap] = useState<Record<string, number>>({});

  const fetchUsers = async () => {
    setLoading(true);
    let query = supabase.from('u_profiles').select('*').order('created_at', { ascending: false });
    if (userTypeFilter) query = query.eq('user_type', userTypeFilter);
    if (groupIdFilter) query = query.eq('group_id', groupIdFilter);
    if (dateRange && dateRange[0]) query = query.gte('created_at', dateRange[0] + ' 00:00:00');
    if (dateRange && dateRange[1]) query = query.lte('created_at', dateRange[1] + ' 23:59:59');
    // 用户ID筛选
    let usersResult = await query;
    let users = usersResult.data || [];
    if (!usersResult.error && users.length > 0) {
      if (userId) {
        users = users.filter((u: any) => u.user_id && u.user_id.includes(userId));
      }
      if (userEmail) {
        users = users.filter((u: any) => u.email && u.email.includes(userEmail));
      }
      if (userAddress) {
        users = users.filter((u: any) => u.address && u.address.includes(userAddress));
      }
      const { data: allUsers } = await supabase.from('u_profiles').select('invite_code,email');
      const inviteMap = (allUsers || []).reduce((acc: any, cur: any) => {
        if (cur.invite_code) acc[cur.invite_code] = cur.email;
        return acc;
      }, {});
      // 反查邮箱对应的邀请码
      let filteredUsers = users;
      if (inviterFilter) {
        // 找到所有邮箱包含inviterFilter的用户的邀请码
        const matchedInviteCodes = (allUsers || [])
          .filter(u => u.email && u.email.includes(inviterFilter))
          .map(u => u.invite_code);
        filteredUsers = users.filter(u => matchedInviteCodes.includes(u.inviter));
      }
      // 查询所有用户的可用资产
      const userIds = filteredUsers.map((u: any) => u.user_id);
      let assetMapAll: Record<string, { available: number, frozen: number, locked: number }> = {};
      if (userIds.length > 0) {
        const { data: assets } = await supabase.from('u_user_assets').select('user_id, available_amount, frozen_amount, locked_amount').in('user_id', userIds);
        assetMapAll = (assets || []).reduce((acc: any, cur: any) => {
          acc[cur.user_id] = {
            available: Number(cur.available_amount || 0),
            frozen: Number(cur.frozen_amount || 0),
            locked: Number(cur.locked_amount || 0),
          };
          return acc;
        }, {});
      }
      const dataWithInviter = filteredUsers.map((user: any) => ({
        ...user,
        inviter_email: user.inviter ? inviteMap[user.inviter] || '' : '',
        asset_frozen: assetMapAll[user.user_id]?.frozen ?? 0,
        asset_available: assetMapAll[user.user_id]?.available ?? 0,
        asset_locked: assetMapAll[user.user_id]?.locked ?? 0,
        total_deposit: depositMap[user.user_id] || 0,
        total_withdraw: withdrawMap[user.user_id] || 0,
      }));
      setData(dataWithInviter);
    } else {
      setData([]);
    }
    setLoading(false);
  };

  // 不自动筛选，只有点击筛选按钮才筛选
  useEffect(() => {
    // 页面加载时自动拉取全部数据
    fetchUsers();
    // 拉取group_id选项
    (async () => {
      const { data: groups } = await supabase.from('u_groups').select('group_id').order('group_id', { ascending: true });
      setGroupOptions((groups || []).map((g: any) => g.group_id?.toString()).filter(Boolean));
    })();
    // 拉取设备数量统计
    (async () => {
      const { data: devices } = await supabase.from('u_user_device').select('user_id,status');
      const map: Record<string, { total: number, wait: number, success: number, fail: number }> = {};
      (devices || []).forEach((d: any) => {
        if (!map[d.user_id]) map[d.user_id] = { total: 0, wait: 0, success: 0, fail: 0 };
        map[d.user_id].total++;
        if (d.status === 0 || d.status === '0') map[d.user_id].wait++;
        else if (d.status === 1 || d.status === '1') map[d.user_id].success++;
        else if (d.status === 2 || d.status === '2') map[d.user_id].fail++;
      });
      setDeviceCountMap(map);
    })();
    // 拉取充油和提取(UCDN)统计
    (async () => {
      const { data: deposits } = await supabase.from('u_transactions').select('user_id,value').eq('type', 'deposit').eq('token_symbol', 'USDT').eq('status', 'success');
      const { data: withdraws } = await supabase.from('u_transactions').select('user_id,value').eq('type', 'withdraw').eq('token_symbol', 'UCDN').eq('status', 'success');
      const depositMap: Record<string, number> = {};
      const withdrawMap: Record<string, number> = {};
      (deposits || []).forEach((d: any) => {
        depositMap[d.user_id] = (depositMap[d.user_id] || 0) + Number(d.value || 0);
      });
      (withdraws || []).forEach((w: any) => {
        withdrawMap[w.user_id] = (withdrawMap[w.user_id] || 0) + Number(w.value || 0);
      });
      setDepositMap(depositMap);
      setWithdrawMap(withdrawMap);
    })();
    // eslint-disable-next-line
  }, []);

  const handleFilter = () => {
    setFiltering(true);
    fetchUsers().then(() => setFiltering(false));
  };

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 150, ellipsis: true, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 300, ellipsis: true },
    { title: '用户邮箱', dataIndex: 'email', key: 'email', width: 170, ellipsis: true },
    { title: '邀请码', dataIndex: 'invite_code', key: 'invite_code', width: 80, ellipsis: true },
    { title: '钱包地址', dataIndex: 'address', key: 'wallet_address', width: 370, ellipsis: true, render: (text: string) => <span style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{text}</span> },
    { title: '组ID', dataIndex: 'group_id', key: 'group_id', width: 60, ellipsis: true },
    { title: '邀请人', dataIndex: 'inviter_email', key: 'inviter_email', width: 170, ellipsis: true },
    { title: '用户类型', dataIndex: 'user_type', key: 'user_type', width: 100, ellipsis: true, render: (type: number) => <Tag color={userTypeColor[type]}>{userTypeMap[type]}</Tag> },
    { title: '设备数(等待/成功/失败)', key: 'device_count', width: 180, align: 'center' as const,
      render: (_: any, record: any) => {
        const c = deviceCountMap[record.user_id] || { total: 0, wait: 0, success: 0, fail: 0 };
        return `${c.total}（${c.wait}/${c.success}/${c.fail}）`;
      }
    },
    { title: '总资产(可用/锁定)', key: 'asset_group', width: 150, align: 'center' as const,
      render: (_: any, record: any) => {
        const available = record.asset_available ?? 0;
        const locked = record.asset_locked ?? 0;
        return `${available + locked}（${available}/${locked}）`;
      }
    },
    { title: '充油总额', dataIndex: 'total_deposit', key: 'total_deposit', width: 100, align: 'center' as const,
      render: (value: number) => `${value}`
    },
    { title: '提取总额', dataIndex: 'total_withdraw', key: 'total_withdraw', width: 100, align: 'center' as const,
      render: (value: number) => `${value} UCDN`
    },
  ];

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }} size={[8, 8]}>
        <Select
          allowClear
          style={{ width: 140 }}
          placeholder="用户类型"
          value={userTypeFilter}
          onChange={setUserTypeFilter}
        >
          <Option value="0">普通用户</Option>
          <Option value="1">大节点POW</Option>
          <Option value="2">小节点POS</Option>
        </Select>
        <Select
            allowClear
            style={{ width: 120 }}
            placeholder="组ID"
            value={groupIdFilter}
            onChange={setGroupIdFilter}
        >
          {groupOptions.map(id => (
              <Option key={id} value={id}>{id}</Option>
          ))}
        </Select>
        <Input
          style={{ width: 180 }}
          placeholder="邀请人邮箱"
          value={inviterFilter}
          onChange={e => setInviterFilter(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="用户ID"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="用户邮箱"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 180 }}
          placeholder="钱包地址"
          value={userAddress}
          onChange={e => setUserAddress(e.target.value)}
          allowClear
        />
        <DatePicker.RangePicker
          style={{ width: 360 }}
          placeholder={['开始时间', '结束时间']}
          value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
          onChange={dates => setDateRange(
            dates && dates[0] && dates[1]
              ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
              : null
          )}
          allowClear
        />
        <Button type="primary" onClick={handleFilter} loading={filtering}>筛选</Button>
        <Button onClick={() => { setUserTypeFilter(undefined); setInviterFilter(''); setGroupIdFilter(undefined); setUserId(''); setUserEmail(''); setUserAddress(''); setDateRange(null); fetchUsers(); }}>重置</Button>
      </Space>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1800 }}
        size="small"
        style={{ minWidth: 1200 }}
      />
    </div>
  );
};

export default UserList;
